import React, { useEffect, useState } from 'react';
import { User as UserIcon } from 'lucide-react';
import { USER_TIERS } from '../../constants';

interface UserAvatarProps {
    photoURL?: string | null;
    tier?: number;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ photoURL, tier = 0, size = 'md', className = '' }) => {
    const tierData = USER_TIERS.find(t => t.level === tier);
    const borderColor = tierData ? tierData.color : 'transparent';
    const [photoFailed, setPhotoFailed] = useState(false);

    useEffect(() => {
        setPhotoFailed(false);
    }, [photoURL]);

    const sizeClasses = {
        xs: 'w-6 h-6',
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16',
    };

    const iconSizes = {
        xs: 12,
        sm: 14,
        md: 16,
        lg: 20,
        xl: 24,
    };

    const borderWidths = {
        xs: '2px',
        sm: '2.5px',
        md: '3px',
        lg: '3px',
        xl: '3.5px',
    };

    const glowSizes = {
        xs: 6,
        sm: 8,
        md: 10,
        lg: 12,
        xl: 16,
    };

    const showPhoto = !!photoURL && !photoFailed;

    return (
        <div
            className={`relative inline-flex items-center justify-center rounded-xl ${className}`}
            style={{
                padding: borderWidths[size],
                background: tier > 0
                    ? `linear-gradient(135deg, ${borderColor}, ${borderColor}bb)`
                    : 'rgba(255,255,255,0.1)',
                boxShadow: tier > 0
                    ? `0 0 ${glowSizes[size]}px ${borderColor}88`
                    : 'none',
            }}
        >
            <div className={`${sizeClasses[size]} rounded-lg bg-[var(--bg-base)] flex items-center justify-center overflow-hidden`}>
                {showPhoto ? (
                    <img
                        src={photoURL}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={() => setPhotoFailed(true)}
                    />
                ) : (
                    <UserIcon size={iconSizes[size]} className="text-[var(--text-secondary)]" />
                )}
            </div>
        </div>
    );
};

export default UserAvatar;
